import { cn } from "@/lib/utils";
import { getConfidenceLevelLabel } from "@/lib/display-labels";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import type { ObjectiveAxis } from "../objective/objective-meta";
import { fmtMetric, fmtPp, objDelta, objDeltaValue, objMetric, oeeDeltaValue } from "./metrics";
import { DeviationPlaceholder, DeviationTrack } from "./charts/DeviationTrack";

export interface RankingRowProps {
  batch: AutoBatchResponse;
  candidate: AutoCandidate;
  baseline: AutoCandidate | null;
  axis: ObjectiveAxis;
  /** A faixa de OEE some quando o objetivo já é o OEE (seria a mesma faixa duas vezes). */
  showOee: boolean;
  gridClass: string;
  objDomain: number;
  oeeDomain: number;
  onOpen: (simulationId: string) => void;
}

// Uma linha do ranking: identidade à esquerda, desvio vs base à direita.
// O número da métrica saiu da linha e desceu para a legenda cinza junto da confiança: repetido em
// coluna, ele era a "parede de números" que este redesenho veio desmontar — quem compara valores absolutos
// abre a tabela ("Ver como tabela"), quem compara candidatos lê as faixas.
export function CandidateRankingRow({
  batch,
  candidate: c,
  baseline,
  axis,
  showOee,
  gridClass,
  objDomain,
  oeeDomain,
  onOpen,
}: RankingRowProps) {
  const delta = objDelta(c, baseline, batch);
  const rawDelta = objDeltaValue(c, baseline, batch);
  const oeeDelta = oeeDeltaValue(c, baseline);
  // Spec 079 — o nível EXIBIDO, o mesmo que `RecommendationHero` e `CompareCandidates` mostram
  // nesta MESMA página. Ler `confidenceLevel` cru aqui fazia a linha do ranking dizer "confiança
  // alta" a poucos pixels de um herói dizendo "Baixa · pelo histórico desta linha".
  const level = c.displayedConfidenceLevel ?? c.confidenceLevel;
  const confidence = level ? getConfidenceLevelLabel(level) : null;

  return (
    <button
      type="button"
      onClick={() => onOpen(c.simulationId)}
      className={cn(
        gridClass,
        "w-full items-start px-2 py-2 text-left transition-colors hover:bg-bg-muted-rd",
        // O vencedor se distingue por fundo + selo, não por moldura: a moldura cortava a régua
        // vertical em pedaços e desalinhava a linha das vizinhas.
        c.isWinner && "bg-brand-50"
      )}
    >
      <Identity
        rank={c.isWinner ? "1" : (c.rankPosition?.toString() ?? "—")}
        name={c.label ?? "Candidato"}
        caption={
          c.isWinner
            ? null
            : [
                confidence ? `confiança ${confidence.toLowerCase()}` : null,
                fmtMetric(objMetric(c, batch)),
              ]
                .filter(Boolean)
                .join(" · ")
        }
        recommended={c.isWinner}
      />

      <span className="flex h-5 items-center">
        <DeviationTrack
          value={rawDelta}
          domain={objDomain}
          positive={delta?.positive ?? false}
          label={delta?.text ?? "—"}
          ariaLabel={`${axis.short}: ${delta?.text ?? "sem dado"} em relação à base`}
        />
      </span>

      {showOee ? (
        <span className="flex h-5 items-center">
          <DeviationTrack
            value={oeeDelta}
            domain={oeeDomain}
            positive={(oeeDelta ?? 0) >= 0}
            label={fmtPp(oeeDelta)}
            // "pp" é abreviação de tela; quem ouve a tela recebe a palavra por extenso.
            ariaLabel={`OEE: ${oeeDelta == null ? "sem dado" : `${oeeDelta.toFixed(1)} pontos`} em relação à base`}
          />
        </span>
      ) : null}
    </button>
  );
}

/**
 * Candidato que falhou. Fica na MESMA lista, com trilhos apagados no lugar das faixas — tirá-lo
 * dali sugere que o motor avaliou só o que deu certo, e uma linha "zerada" o faria parecer um
 * candidato ruim em vez de um candidato sem resposta.
 */
export function CandidateRankingFailedRow({
  candidate,
  showOee,
  gridClass,
}: {
  candidate: AutoCandidate;
  showOee: boolean;
  gridClass: string;
}) {
  return (
    <div className={cn(gridClass, "items-start px-2 py-2")}>
      <Identity rank="!" name={candidate.label ?? "Candidato"} caption={null} muted />
      <span className="flex h-5 items-center">
        <DeviationPlaceholder label="não avaliado — falhou" />
      </span>
      {showOee ? (
        <span className="flex h-5 items-center">
          <DeviationPlaceholder />
        </span>
      ) : null}
    </div>
  );
}

function Identity({
  rank,
  name,
  caption,
  recommended,
  muted,
}: {
  rank: string;
  name: string;
  caption: string | null;
  recommended?: boolean;
  muted?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-start gap-2.5">
      <span className="w-3 shrink-0 pt-0.5 text-right text-[11px] tabular-nums text-text-tertiary">
        {rank}
      </span>
      <span className="min-w-0">
        <span
          className={cn(
            "block truncate text-[13px] font-medium",
            muted ? "text-text-secondary" : "text-text-primary"
          )}
        >
          {name}
        </span>
        {recommended ? (
          <span className="block text-[11px] font-medium text-brand-600">recomendado</span>
        ) : caption ? (
          <span className="block truncate text-[11px] text-text-tertiary">{caption}</span>
        ) : null}
      </span>
    </span>
  );
}
